import { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Camera, Upload, X, Image as ImageIcon } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ProofImageUpload({ cycleId, machineId, siteId, companyId, onUploadComplete }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext) && f.size <= 10 * 1024 * 1024;
    });
    if (valid.length < selected.length) toast.error('Some files skipped (unsupported type or >10MB)');
    setFiles(prev => [...prev, ...valid]);
    valid.forEach(f => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews(prev => [...prev, { name: f.name, url: ev.target.result }]);
      reader.readAsDataURL(f);
    });
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const uploadAll = async () => {
    if (!files.length) return;
    setUploading(true);
    let success = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (cycleId) fd.append('cycle_id', cycleId);
        if (machineId) fd.append('machine_id', machineId);
        if (siteId) fd.append('site_id', siteId);
        if (companyId) fd.append('company_id', companyId);
        await api.post('/uploads/proof-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        success++;
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    if (success > 0) {
      toast.success(`${success} proof image${success > 1 ? 's' : ''} uploaded`);
      setFiles([]);
      setPreviews([]);
      if (onUploadComplete) onUploadComplete();
    }
  };

  return (
    <div className="space-y-3" data-testid="proof-upload">
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} className="text-xs" data-testid="proof-select-btn">
          <Camera size={14} className="mr-1" /> Add Proof Images
        </Button>
        {files.length > 0 && (
          <Button type="button" size="sm" onClick={uploadAll} disabled={uploading} className="text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="proof-upload-btn">
            <Upload size={14} className="mr-1" /> {uploading ? 'Uploading...' : `Upload ${files.length} Image${files.length > 1 ? 's' : ''}`}
          </Button>
        )}
        <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic" multiple className="hidden" onChange={handleFileSelect} />
      </div>
      {previews.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {previews.map((p, i) => (
            <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
              <button onClick={() => removeFile(i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`proof-remove-${i}`}>
                <X size={12} />
              </button>
              <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] px-1 truncate">{p.name}</p>
            </div>
          ))}
        </div>
      )}
      {!files.length && (
        <p className="text-xs text-slate-400 flex items-center gap-1"><ImageIcon size={12} /> Optional: attach proof photos of tooth point condition</p>
      )}
    </div>
  );
}
